/**
 * Skeleton — placeholder for loading content. Shape matches the final element so layout
 * doesn't shift when data arrives. Subtle shimmer; brand-tinted neutral.
 *
 * @example
 * <Skeleton className="h-4 w-32" />              // single shape
 * <Skeleton className="h-12 w-full rounded-lg" /> // card-like
 * <Skeleton.Text lines={3} />                     // multi-line text block
 * <Skeleton.Row />                                // a generic table row
 */
import { ReactNode } from 'react';

interface BaseProps {
  className?: string;
  /** Aria label override (default: "Loading") */
  label?: string;
  children?: ReactNode;
  /** Suppress role/aria when nested inside another loading region */
  decorative?: boolean;
}

const SkeletonRoot = ({ className = '', label = 'Loading', children, decorative = false }: BaseProps) => (
  <div
    role={decorative ? undefined : 'status'}
    aria-label={decorative ? undefined : label}
    aria-busy={decorative ? undefined : true}
    aria-hidden={decorative ? true : undefined}
    className={`animate-pulse rounded-md bg-dark-invirtus-soft ${className}`}
  >
    {children && <span className="sr-only">{children}</span>}
  </div>
);

interface TextProps {
  /** Number of lines */
  lines?: number;
  className?: string;
  decorative?: boolean;
}

const SkeletonText = ({ lines = 3, className = '', decorative = false }: TextProps) => (
  <div
    className={`flex flex-col gap-2 ${className}`}
    role={decorative ? undefined : 'status'}
    aria-label={decorative ? undefined : 'Loading text'}
    aria-busy={decorative ? undefined : true}
    aria-hidden={decorative ? true : undefined}
  >
    {Array.from({ length: lines }).map((_, i) => (
      <div
        key={i}
        className="animate-pulse rounded bg-dark-invirtus-soft h-3"
        // last line is shorter for natural look
        style={{ width: i === lines - 1 ? '70%' : '100%' }}
      />
    ))}
  </div>
);

const SkeletonRow = ({ className = '' }: { className?: string }) => (
  <div
    className={`flex items-center gap-3 px-4 py-3 ${className}`}
    role="status"
    aria-label="Loading row"
    aria-busy="true"
  >
    <div className="animate-pulse rounded-full bg-dark-invirtus-soft h-8 w-8 shrink-0" />
    <div className="flex flex-col gap-2 flex-1 min-w-0">
      <div className="animate-pulse rounded bg-dark-invirtus-soft h-3 w-1/3" />
      <div className="animate-pulse rounded bg-dark-invirtus-soft h-2.5 w-1/2" />
    </div>
    <div className="animate-pulse rounded-full bg-dark-invirtus-soft h-5 w-16 shrink-0" />
  </div>
);

const SkeletonCard = ({ className = '' }: { className?: string }) => (
  <div
    className={`rounded-xl border border-light-lila/40 bg-light p-4 flex flex-col gap-3 ${className}`}
    role="status"
    aria-label="Loading card"
    aria-busy="true"
  >
    <div className="animate-pulse rounded bg-dark-invirtus-soft h-4 w-1/2" />
    <div className="animate-pulse rounded bg-dark-invirtus-soft h-3 w-full" />
    <div className="animate-pulse rounded bg-dark-invirtus-soft h-3 w-3/4" />
  </div>
);

export const Skeleton = Object.assign(SkeletonRoot, {
  Text: SkeletonText,
  Row: SkeletonRow,
  Card: SkeletonCard,
});
